/**
 * LA PREGUNTA AL TRABAJADOR, sin React: cuándo se hace, en qué paso está y qué
 * se guarda con el parte.
 *
 * Decisión del usuario (21-09-2026): al elegir una línea donde ya se avisó de una
 * plaga, a quien muestrea se le enseña la última foto y se le pregunta «¿Es lo
 * mismo que ves ahora?» → Sí / No, es otra cosa / No lo sé; y si es que sí,
 * «¿Cómo está ahora?» → Mejor / Igual / Peor / Ya no hay / No lo sé. SIN la
 * palabra «foco».
 *
 * ES UNA PISTA, NO UN ENLACE. Lo que conteste viaja con el parte y quien
 * confirma lo ve; nada se une a nada hasta que alguien confirma.
 *
 * Y LA PREGUNTA NO SE OMITE EN SILENCIO (D20, 22-09-2026): se espera hasta
 * `FOLLOW_UP_MAX_WAIT_MS` con una pantalla que dice «comprobando esta línea», y
 * si aun así no llega, el parte guarda POR QUÉ (`followup_skip_reason`) para que
 * quien confirma lo vea y para saber cuántas preguntas se pierden y dónde.
 * También se pregunta por un parte SIN confirmar de la misma línea (no hay foco
 * todavía): la pista va en `followup_session_id`.
 */
import type { FollowUpAnswer } from "@/lib/health/focusLinking";

/** Tope de espera a los focos del sector al llegar al resumen. Antes 1,5 s: con cobertura floja se perdía la pregunta. */
export const FOLLOW_UP_MAX_WAIT_MS = 8000;

/** «No lo sé» (D19) es una respuesta válida: no se guarda veredicto, pero sí que se contestó. */
export type FollowUpVerdict = "better" | "same" | "worse" | "gone" | "unknown";
export type FollowUpStage = "off" | "waiting" | "ask_same" | "ask_how" | "done";
export type FollowUpSkipReason = "timeout" | "error" | "offline";

export interface FollowUpCandidate {
  /** El sitio ya confirmado; `null` cuando la pista es un parte sin confirmar. */
  focusId: string | null;
  /** Un parte de la misma línea aún sin confirmar (no hay foco todavía). */
  sessionId?: string | null;
  /** La línea del sitio y a cuántas líneas está de la elegida (0 = la misma; 1–2 = al lado). */
  lineNumber?: number | null;
  lineGap?: number;
  pestName: string;
  /** Fecha del último muestreo de ese sitio, `yyyy-MM-dd`. */
  lastDate: string;
  daysAgo: number;
  photoUrl: string | null;
}

export interface FollowUpState {
  /** Focos encendidos para la empresa y es el camino del trabajador. */
  enabled: boolean;
  status: "off" | "loading" | "ready" | "failed";
  /** El sitio sobre el que se contesta: el único, o el que eligió entre varios (D24). */
  candidate: FollowUpCandidate | null;
  /** Se agotó la espera (o se decidió omitir): ya no se pregunta en este parte. */
  skipped: boolean;
  /** Por qué se omitió, cuando fue por no poder preguntar (no por no haber nada que preguntar). */
  skipReason?: FollowUpSkipReason | null;
  answer: FollowUpAnswer | null;
  verdict: FollowUpVerdict | null;
  /** El parte nace de una tarea de seguimiento: ya se sabe de qué sitio se habla. */
  fromTask: boolean;
}

export function followUpStage(s: FollowUpState): FollowUpStage {
  if (!s.enabled || s.skipped) return "off";
  if (s.status === "loading") return "waiting";
  if (s.status !== "ready" || !s.candidate) return "off";
  // Desde una tarea de seguimiento la primera pregunta sobra: vino a mirar ESE sitio.
  const answer = s.fromTask ? (s.answer ?? "same") : s.answer;
  if (answer == null) return "ask_same";
  if (answer === "same" && s.verdict == null) return "ask_how";
  return "done";
}

/**
 * Lo que se añade al INSERT del parte. Vacío si no se preguntó ni hubo motivo:
 * un parte sin pista es exactamente el parte de siempre, y un bundle viejo no
 * las manda.
 */
export function followUpColumns(s: FollowUpState, taskId: string | null): Record<string, string> {
  if (!s.enabled) return {};
  if (s.skipped && s.skipReason) return { followup_skip_reason: s.skipReason };
  if (!s.candidate) return {};
  const answer = s.fromTask ? (s.answer ?? "same") : s.answer;
  if (answer == null) return {};
  const out: Record<string, string> = { followup_answer: answer };
  if (s.candidate.focusId) out.followup_focus_id = s.candidate.focusId;
  else if (s.candidate.sessionId) out.followup_session_id = s.candidate.sessionId;
  else return {};
  if (answer === "same" && s.verdict && s.verdict !== "unknown") out.evolution_verdict = s.verdict;
  if (s.fromTask && taskId) out.followup_task_id = taskId;
  return out;
}

/** «Hoy», «ayer» o «hace N días»: tres claves, sin plurales por cantidad (el árabe tiene seis). */
export function seenKey(daysAgo: number): "seenToday" | "seenYesterday" | "seenDaysAgo" {
  if (daysAgo <= 0) return "seenToday";
  return daysAgo === 1 ? "seenYesterday" : "seenDaysAgo";
}
